(() => {
  const controllers = new WeakMap();
  const selector = '[data-rs-hero], [data-rs-menu-toggle], [data-rs-popup], [data-rs-anchor-link]';
  const all = (root) => [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
  const designMode = () => Boolean(window.Shopify?.designMode) || document.documentElement.classList.contains('shopify-design-mode');
  const safeRead = (storage, key) => { try { return window[storage].getItem(key); } catch { return null; } };
  const safeWrite = (storage, key, value) => { try { window[storage].setItem(key, value); } catch {} };

  function applyTextFragments(root = document) {
    const configs = [...(root.matches?.('[data-rs-fragment-config]') ? [root] : []), ...root.querySelectorAll('[data-rs-fragment-config]')];
    configs.forEach((config) => {
      const needle = config.dataset.rsFragmentText;
      const styleId = config.dataset.rsStyleId;
      const wanted = Number(config.dataset.rsFragmentOccurrence || 0);
      if (!needle || !styleId) return;
      let targets;
      try { targets = [...config.closest('.shopify-section')?.querySelectorAll(config.dataset.rsFragmentSelector) || []]; }
      catch { return; }
      let occurrence = 0;
      targets.forEach((target) => {
        const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT, {
          acceptNode: (node) => node.parentElement?.closest('script,style,[data-rs-fragment-style]') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
        });
        const nodes = [];
        while (walker.nextNode()) nodes.push(walker.currentNode);
        nodes.forEach((node) => {
          const source = node.nodeValue || '';
          let cursor = 0;
          let index = source.indexOf(needle);
          let changed = false;
          const fragment = document.createDocumentFragment();
          while (index >= 0) {
            occurrence += 1;
            fragment.append(source.slice(cursor, index));
            const matched = source.slice(index, index + needle.length);
            if (wanted === 0 || wanted === occurrence) {
              const span = document.createElement('span');
              span.dataset.rsFragmentStyle = styleId;
              span.textContent = matched;
              fragment.append(span);
              changed = true;
            } else fragment.append(matched);
            cursor = index + needle.length;
            index = source.indexOf(needle, cursor);
          }
          if (changed) {
            fragment.append(source.slice(cursor));
            node.replaceWith(fragment);
          }
        });
      });
    });
  }

  function initialize(root = document) {
    all(root).forEach((element) => {
      if (controllers.has(element)) return;
      const abort = new AbortController();
      const cleanups = [() => abort.abort()];
      const on = (target, type, handler) => target.addEventListener(type, handler, { signal: abort.signal });
      let api = {};

      if (element.matches('[data-rs-anchor-link]')) {
        on(element, 'click', (event) => {
          const anchor = element.dataset.rsAnchorLink;
          const target = anchor && document.getElementById(anchor);
          const url = new URL(element.href, window.location.href);
          if (!target || url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
          event.preventDefault();
          const header = document.querySelector('.rs-header');
          const headerOffset = header && getComputedStyle(header).position === 'sticky' ? header.offsetHeight : 0;
          const top = target.getBoundingClientRect().top + window.scrollY - headerOffset;
          window.scrollTo({ top, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
          if (window.location.hash !== `#${anchor}`) history.pushState(null, '', `#${anchor}`);
        });
      }

      if (element.matches('[data-rs-hero]')) {
        const slides = [...element.querySelectorAll('[data-rs-slide]')];
        let index = 0;
        let timer;
        const show = (next) => {
          index = (next + slides.length) % slides.length;
          slides.forEach((slide, i) => {
            slide.classList.toggle('is-active', i === index);
            slide.setAttribute('aria-hidden', String(i !== index));
            slide.querySelectorAll('video').forEach((video) => {
              if (i !== index) video.pause();
              else video.play()?.catch(() => {});
            });
          });
          const current = element.querySelector('[data-rs-current]');
          if (current) current.textContent = String(index + 1).padStart(2, '0');
        };
        const stop = () => window.clearInterval(timer);
        if (slides.length > 1 && element.dataset.autoplay === 'true' && !designMode() && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
          timer = window.setInterval(() => show(index + 1), Number(element.dataset.interval || 7) * 1000);
        }
        cleanups.push(stop);
        api.selectBlock = (id) => {
          const i = slides.findIndex(slide => slide.dataset.rsBlockId === id);
          if (i >= 0) { stop(); show(i); }
        };
        api.selectTextItem = (item) => { stop(); show(Math.min(Math.max(Number(item) - 1, 0), slides.length - 1)); };
      }

      if (element.matches('[data-rs-menu-toggle]')) {
        const menu = document.getElementById(element.getAttribute('aria-controls'));
        if (menu) {
          const close = () => { element.setAttribute('aria-expanded', 'false'); menu.classList.remove('is-open'); };
          on(element, 'click', () => {
            const open = element.getAttribute('aria-expanded') !== 'true';
            element.setAttribute('aria-expanded', String(open)); menu.classList.toggle('is-open', open);
          });
          on(menu, 'click', (event) => { if (event.target.closest('a')) close(); });
          on(document, 'keydown', (event) => {
            if (event.key === 'Escape' && element.getAttribute('aria-expanded') === 'true') { close(); element.focus(); }
          });
          on(document, 'click', (event) => { if (!menu.contains(event.target) && !element.contains(event.target)) close(); });
          const breakpoint = matchMedia('(min-width: 901px)');
          on(breakpoint, 'change', close);
          cleanups.push(close);
        }
      }

      if (element.matches('[data-rs-popup]')) {
        const key = 'rs-popup-' + element.dataset.rsPopup;
        const form = element.querySelector('form');
        const repeatDays = Number(element.dataset.repeatDays || 0);
        let previousFocus;
        let timer;
        const open = () => {
          if (!element.hidden) return;
          previousFocus = document.activeElement;
          element.hidden = false; element.setAttribute('aria-hidden', 'false');
          document.documentElement.classList.add('rs-popup-open');
          (element.querySelector('input[type=email]') || element.querySelector('.rs-popup__close'))?.focus({ preventScroll: true });
        };
        const close = (remember = true, restore = true) => {
          const wasOpen = !element.hidden;
          element.hidden = true; element.setAttribute('aria-hidden', 'true');
          if (!document.querySelector('[data-rs-popup]:not([hidden])')) document.documentElement.classList.remove('rs-popup-open');
          if (remember && !designMode() && repeatDays > 0) safeWrite('localStorage', key, String(Date.now() + repeatDays * 86400000));
          if (restore && wasOpen && previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
        };
        element.querySelectorAll('[data-rs-popup-close]').forEach(button => on(button, 'click', () => close()));
        on(document, 'keydown', (event) => {
          if (element.hidden) return;
          if (event.key === 'Escape') { event.preventDefault(); close(); }
          if (event.key === 'Tab') {
            const focusable = [...element.querySelectorAll('button,a[href],input:not([type=hidden]),[tabindex="0"]')].filter(el => !el.disabled && el.getClientRects().length);
            const first = focusable[0], last = focusable.at(-1);
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
          }
        });
        if (form) on(form, 'submit', () => safeWrite('sessionStorage', 'rs-submitted-form', form.id));
        const returned = form && safeRead('sessionStorage', 'rs-submitted-form') === form.id &&
          element.querySelector('[data-rs-form-success], [data-rs-form-error]');
        if (returned) { safeWrite('sessionStorage', 'rs-submitted-form', ''); open(); }
        else if (!designMode() && (repeatDays === 0 || Date.now() > Number(safeRead('localStorage', key) || 0))) {
          timer = window.setTimeout(open, Number(element.dataset.delay || 0) * 1000);
        }
        cleanups.push(() => { window.clearTimeout(timer); close(false, false); });
        api.open = open;
        api.close = () => { window.clearTimeout(timer); close(false); };
      }
      controllers.set(element, { ...api, destroy: () => cleanups.forEach(fn => fn()) });
    });
  }
  const ready = () => { initialize(); applyTextFragments(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true });
  else ready();
  const selectedTextStyles = new Map();
  const previewTextStyle = (section, blockId) => {
    const marker = [...section.querySelectorAll('[data-rs-style-id]')].find(el => el.dataset.rsStyleId === blockId);
    if (!marker) return;
    const popup = section.querySelector('[data-rs-popup]');
    if (popup) controllers.get(popup)?.open?.();
    const hero = section.querySelector('[data-rs-hero]');
    if (hero) controllers.get(hero)?.selectTextItem?.(marker.dataset.rsTextItem);
    const toggle = section.querySelector('[data-rs-menu-toggle]');
    if (marker.dataset.rsTextStyle === 'nav' && toggle && matchMedia('(max-width: 900px)').matches && toggle.getAttribute('aria-expanded') !== 'true') toggle.click();
  };
  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
    applyTextFragments(event.target);
    const blockId = selectedTextStyles.get(event.target.id);
    if (blockId) previewTextStyle(event.target, blockId);
  });
  document.addEventListener('shopify:section:unload', (event) => all(event.target).forEach(el => { controllers.get(el)?.destroy(); controllers.delete(el); }));
  document.addEventListener('shopify:section:select', (event) => all(event.target).forEach(el => controllers.get(el)?.open?.()));
  document.addEventListener('shopify:section:deselect', (event) => {
    selectedTextStyles.delete(event.target.id);
    all(event.target).forEach(el => controllers.get(el)?.close?.());
  });
  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest('.shopify-section');
    const blockId = event.detail?.blockId;
    if (section && blockId) {
      selectedTextStyles.set(section.id, blockId);
      previewTextStyle(section, blockId);
    }
    const hero = event.target.closest('[data-rs-hero]');
    if (hero) controllers.get(hero)?.selectBlock?.(blockId);
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const section = event.target.closest('.shopify-section');
    if (section) selectedTextStyles.delete(section.id);
  });
})();
